import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  FileSignature, Plus, Search, AlertTriangle, Calendar, Loader2, RefreshCcw,
  CheckCircle2, Trash2, Eye,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * P6 — Contract Center
 * URL: /admin/procurement/contracts
 */

const STATUS_META = {
  draft:       { label: 'Draft',       color: 'bg-slate-100 text-slate-600' },
  active:      { label: 'Active',      color: 'bg-emerald-100 text-emerald-700' },
  expiring:    { label: 'Expiring',    color: 'bg-amber-100 text-amber-700' },
  expired:     { label: 'Expired',     color: 'bg-rose-100 text-rose-700' },
  terminated:  { label: 'Terminated',  color: 'bg-slate-200 text-slate-500' },
};


const ContractsList = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const authHeader = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const [form, setForm] = useState({
    supplier_id: '', title: '', reference_number: '',
    start_date: '', end_date: '', currency: 'AED', total_value: 0,
    payment_terms: '', terms: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filterStatus ? `?status=${filterStatus}` : '';
      const r = await axios.get(`${API_URL}/api/procurement/contracts${q}`, authHeader);
      setContracts(r.data || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.message, variant: 'destructive' });
    } finally { setLoading(false); }
  }, [authHeader, filterStatus, toast]);

  useEffect(() => { load(); }, [load]);

  const createContract = useCallback(async () => {
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/procurement/contracts`, {
        ...form,
        total_value: Number(form.total_value) || 0,
      }, authHeader);
      toast({ title: 'Contract created' });
      setDialogOpen(false);
      setForm({ supplier_id: '', title: '', reference_number: '', start_date: '',
        end_date: '', currency: 'AED', total_value: 0, payment_terms: '', terms: '' });
      await load();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setCreating(false); }
  }, [form, authHeader, toast, load]);

  const deleteContract = useCallback(async (id) => {
    if (!window.confirm('Delete this contract?')) return;  // eslint-disable-line no-alert
    try {
      await axios.delete(`${API_URL}/api/procurement/contracts/${id}`, authHeader);
      toast({ title: 'Contract deleted' });
      await load();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.message, variant: 'destructive' });
    }
  }, [authHeader, toast, load]);

  const runExpirySweep = useCallback(async () => {
    setSweeping(true);
    try {
      const r = await axios.post(`${API_URL}/api/procurement/contracts/sweep/expiry?warn_days=30`, {}, authHeader);
      toast({
        title: 'Expiry sweep completed',
        description: `Expired: ${r.data.expired} · Expiring: ${r.data.expiring} · Alerts: ${r.data.alerts_created}`,
      });
      await load();
    } catch (err) {
      toast({ title: 'Sweep failed', description: err?.message, variant: 'destructive' });
    } finally { setSweeping(false); }
  }, [authHeader, toast, load]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return contracts;
    return contracts.filter((c) =>
      (c.title || '').toLowerCase().includes(s) ||
      (c.reference_number || '').toLowerCase().includes(s) ||
      (c.supplier_snapshot?.name || '').toLowerCase().includes(s)
    );
  }, [contracts, search]);

  return (
    <div data-testid="contracts-list-page">
      <div className="mb-5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-sky-500/30">
            <FileSignature className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#2B2D42]">Contract Center</h1>
            <p className="text-sm text-[#6C757D]">Supplier contracts, validity and linkage to RFQ/PO.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={runExpirySweep} disabled={sweeping} data-testid="contracts-sweep-btn">
            {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCcw className="w-3.5 h-3.5 mr-1" /> Run expiry sweep</>}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="contracts-new-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Contract</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg" data-testid="contracts-new-dialog">
              <DialogHeader>
                <DialogTitle>New Contract</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Supplier ID" value={form.supplier_id} onChange={(v) => setForm({ ...form, supplier_id: v })} testId="contract-form-supplier-id" required />
                <FormField label="Reference #" value={form.reference_number} onChange={(v) => setForm({ ...form, reference_number: v })} testId="contract-form-ref" />
                <FormField className="col-span-2" label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testId="contract-form-title" required />
                <FormField label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="contract-form-start" required />
                <FormField label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="contract-form-end" required />
                <FormField label="Currency" value={form.currency} onChange={(v) => setForm({ ...form, currency: v })} testId="contract-form-currency" />
                <FormField label="Total value" type="number" value={form.total_value} onChange={(v) => setForm({ ...form, total_value: v })} testId="contract-form-total" />
                <FormField className="col-span-2" label="Payment terms" value={form.payment_terms} onChange={(v) => setForm({ ...form, payment_terms: v })} testId="contract-form-terms" />
              </div>
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={createContract} disabled={creating || !form.supplier_id || !form.title || !form.start_date || !form.end_date} data-testid="contract-form-submit">
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="mb-4">
        <CardContent className="p-3 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-slate-400" />
          <input
            placeholder="Search by title, supplier or reference…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="contracts-search"
            className="flex-1 text-xs px-2 py-1.5 border-0 bg-transparent focus:outline-none"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="contracts-filter-status"
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white">
            <option value="">All statuses</option>
            {Object.keys(STATUS_META).map((k) => (<option key={k} value={k}>{STATUS_META[k].label}</option>))}
          </select>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-slate-500" data-testid="contracts-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-[#6C757D]" data-testid="contracts-empty">No contracts match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="contracts-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[10px] uppercase font-semibold text-[#6C757D]">
                    <th className="px-3 py-2 text-left">Ref</th>
                    <th className="px-3 py-2 text-left">Title</th>
                    <th className="px-3 py-2 text-left">Supplier</th>
                    <th className="px-3 py-2 text-left">Period</th>
                    <th className="px-3 py-2 text-right">Value</th>
                    <th className="px-3 py-2 text-center">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((c) => {
                    const meta = STATUS_META[c.status] || STATUS_META.draft;
                    const expiringSoon = c.status === 'expiring';
                    return (
                      <tr key={c.id} className="hover:bg-slate-50" data-testid={`contract-row-${c.id}`}>
                        <td className="px-3 py-2 text-xs font-mono">{c.reference_number || '—'}</td>
                        <td className="px-3 py-2 text-sm font-semibold text-[#2B2D42]">{c.title}</td>
                        <td className="px-3 py-2 text-xs text-[#2B2D42]">{c.supplier_snapshot?.name || c.supplier_id.slice(0, 8)}</td>
                        <td className="px-3 py-2 text-xs text-[#6C757D] font-mono">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          {c.start_date} → {c.end_date}
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-mono">{c.currency} {Number(c.total_value).toLocaleString()}</td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${meta.color}`}>
                            {expiringSoon && <AlertTriangle className="w-2.5 h-2.5" />}
                            {c.status === 'active' && <CheckCircle2 className="w-2.5 h-2.5" />}
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button className="text-[#00B4D8] hover:text-[#023E8A] text-xs mr-2" data-testid={`contract-view-${c.id}`} title="View">
                            <Eye className="w-3.5 h-3.5 inline" />
                          </button>
                          <button onClick={() => deleteContract(c.id)} className="text-rose-600 hover:text-rose-700 text-xs" data-testid={`contract-delete-${c.id}`} title="Delete">
                            <Trash2 className="w-3.5 h-3.5 inline" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


const FormField = ({ label, value, onChange, type = 'text', testId, required, className = '' }) => (
  <div className={className}>
    <Label className="text-[10px] uppercase font-semibold text-[#6C757D]">{label}{required && ' *'}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="h-8 text-sm" />
  </div>
);


export default ContractsList;
